import { createFileRoute } from "@tanstack/react-router";
import { AnmeldeSeite } from "@/components/AnmeldeSeite";

/*
 * `/login` — das Ziel jeder abgewiesenen Anmeldung.
 *
 * Der Pfad ist NICHT frei gewählt: der Go-Server leitet fest dorthin
 * (`PfadAnmeldeseite = "/login"` in `internal/api/auth.go`), und zwar auch
 * dann, wenn diese Route fehlt — dann eben ins Not-Found. Er ist damit eine
 * Zusage des Servers, die diese Datei einlöst.
 *
 * ═══ Warum die Form hier schmal bleibt ════════════════════════════════════
 *
 * Als diese Datei entstand, war sie die ERSTE Route mit `validateSearch`, und
 * hier stand „die Filter der Suchsichten sind noch nicht gebaut". Beides ist
 * überholt: `validateSearch` steht inzwischen auch an `__root`, `/suche`,
 * `/verkaufssuche` und `/artikel/$id`, und `/suche` führt 19 Filterparameter
 * (`lib/suche-filter.ts`, gepinnt in dessen Test).
 *
 * Die Form unten bleibt trotzdem schmal, und der Grund ist ein anderer als
 * der damalige: nicht „es gibt noch nichts Größeres", sondern der Server
 * schickt hierher nur zwei optionale Zeichenketten. Ein Vorgriff auf die
 * Filtersichten wäre sie auch heute nicht.
 *
 * Kein `loader` und kein `beforeLoad` — dieselbe Linie wie bei allen anderen
 * Routen dieser SPA: Zustände gehören in die Komponente, die sie anzeigt.
 */

/**
 * Was in der URL dieser Seite stehen darf.
 *
 * ═══ Warum unbekannte Codes NICHT abgewiesen werden ═══════════════════════
 *
 * `error` wird als beliebige Zeichenkette übernommen und NICHT gegen die
 * bekannten Codes geprüft. Das ist Absicht: ein unbekannter Code ist ein
 * Fehler, den der Server gemeldet hat und den diese Fassung nur nicht benennen
 * kann — ihn hier wegzuwerfen hieße, den Nutzer auf einer wortlosen
 * Anmeldeseite stehen zu lassen. Den Rückfallsatz liefert
 * `anmeldeFehlerText`; die Prüfung, dass die Codemengen überhaupt
 * zusammenpassen, macht `internal/api/anmeldefehler_test.go`.
 *
 * Nicht-Zeichenketten werden dagegen zu `undefined`. Der eigene
 * Serialisierer dieser App (`lib/search-params.ts`) JSON-parst Werte bewusst
 * nicht, liefert hier also ohnehin nur Zeichenketten — die Prüfung hält die
 * Zusage trotzdem fest, statt sie vorauszusetzen.
 */
interface AnmeldeSuche {
  error?: string;
  callbackUrl?: string;
}

function alsText(wert: unknown): string | undefined {
  return typeof wert === "string" && wert !== "" ? wert : undefined;
}

/**
 * Benannt und EXPORTIERT statt inline in `createFileRoute`: so ist sie ohne
 * Router prüfbar. `Route.options` auszulesen hinge dagegen an einer internen
 * Form des Routers, die kein Vertrag ist.
 *
 * Der Name des Parameters ist hier die eigentliche Zusage: der Go-Server
 * schreibt `?error=` (`zurAnmeldeseite` in `internal/api/auth.go`), und
 * `internal/api/anmeldefehler_test.go` misst das an der echten Funktion nach.
 */
export function anmeldeSucheLesen(suche: Record<string, unknown>): AnmeldeSuche {
  return {
    error: alsText(suche.error),
    callbackUrl: alsText(suche.callbackUrl),
  };
}

export const Route = createFileRoute("/login")({
  validateSearch: anmeldeSucheLesen,
  component: AnmeldeRoute,
});

function AnmeldeRoute() {
  const { error, callbackUrl } = Route.useSearch();
  return <AnmeldeSeite fehler={error} callbackUrl={callbackUrl} />;
}
